import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import { insightConfigApi } from '../api/InsightConfigApi'
import { SaveInsightConfigFlow } from '../flows/SaveInsightConfigFlow'
import type { InsightConfigListParams } from '../models/insightApi.types'
import type { InsightConfig, InsightConfigMeta } from '../models/insightConfig.types'
import type { WorkbenchAsyncState } from '../models/workbench.types'

import { useInsightsRbac } from './useInsightsRbac'

import { ApiError, type Paginated } from '@/shared/http'

export interface InsightConfigListFilters {
  search: string
  status: string
  outcome: string
  severity: string
  platform: string
}

interface InsightConfigListApiPort {
  list: (
    params?: InsightConfigListParams,
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightConfig>>
  meta: (signal?: AbortSignal) => Promise<InsightConfigMeta>
}

export interface UseInsightConfigListOptions {
  canAuthor?: MaybeRefOrGetter<boolean>
  api?: InsightConfigListApiPort
  flow?: SaveInsightConfigFlow
  limit?: number
}

export interface UseInsightConfigList {
  configs: ComputedRef<readonly InsightConfig[]>
  meta: ComputedRef<InsightConfigMeta | null>
  total: ComputedRef<number>
  offset: ComputedRef<number>
  state: ComputedRef<WorkbenchAsyncState>
  error: ComputedRef<ApiError | null>
  draftFilters: Ref<InsightConfigListFilters>
  deleteBlockedCount: ComputedRef<number>
  load: () => Promise<void>
  applyFilters: () => Promise<void>
  clearFilters: () => Promise<void>
  nextPage: () => Promise<void>
  previousPage: () => Promise<void>
  deleteConfig: (id: number) => Promise<'deleted' | 'blocked' | null>
}

const emptyFilters = (): InsightConfigListFilters => ({
  search: '',
  status: '',
  outcome: '',
  severity: '',
  platform: '',
})

export function useInsightConfigList(
  options: UseInsightConfigListOptions = {},
): UseInsightConfigList {
  const rbac = options.canAuthor === undefined ? useInsightsRbac() : null
  const canAuthor = computed(() =>
    options.canAuthor === undefined
      ? (rbac?.canAuthorInsights.value ?? false)
      : toValue(options.canAuthor),
  )
  const api = options.api ?? insightConfigApi
  const flow = options.flow ?? new SaveInsightConfigFlow()
  const limit = options.limit ?? 20
  const configs = ref<InsightConfig[]>([])
  const meta = ref<InsightConfigMeta | null>(null)
  const total = ref(0)
  const offset = ref(0)
  const state = ref<WorkbenchAsyncState>('idle')
  const error = ref<ApiError | null>(null)
  const draftFilters = ref(emptyFilters())
  const appliedFilters = ref(emptyFilters())
  const deleteBlockedCount = ref(0)
  let generation = 0
  let controller: AbortController | null = null

  function reset(): void {
    generation += 1
    controller?.abort()
    controller = null
    configs.value = []
    total.value = 0
    state.value = 'idle'
    error.value = null
    deleteBlockedCount.value = 0
  }

  function params(): InsightConfigListParams {
    return {
      limit,
      offset: offset.value,
      search: appliedFilters.value.search,
      status: appliedFilters.value.status,
      outcome_category: appliedFilters.value.outcome,
      severity: appliedFilters.value.severity,
      platform: appliedFilters.value.platform,
    }
  }

  async function load(): Promise<void> {
    if (!canAuthor.value) {
      reset()
      return
    }
    const request = ++generation
    controller?.abort()
    const activeController = new AbortController()
    controller = activeController
    state.value = 'loading'
    error.value = null
    try {
      const [page, nextMeta] = await Promise.all([
        api.list(params(), activeController.signal),
        meta.value ? Promise.resolve(meta.value) : api.meta(activeController.signal),
      ])
      if (request !== generation || activeController.signal.aborted) return
      configs.value = page.results
      total.value = page.count
      meta.value = nextMeta
      state.value = page.results.length > 0 ? 'ready' : 'empty'
    } catch (cause) {
      if (request !== generation || activeController.signal.aborted) return
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled') return
      error.value = normalized
      state.value = 'error'
    }
  }

  async function applyFilters(): Promise<void> {
    appliedFilters.value = { ...draftFilters.value }
    offset.value = 0
    await load()
  }

  async function clearFilters(): Promise<void> {
    draftFilters.value = emptyFilters()
    appliedFilters.value = emptyFilters()
    offset.value = 0
    await load()
  }

  async function nextPage(): Promise<void> {
    if (offset.value + limit >= total.value) return
    offset.value += limit
    await load()
  }

  async function previousPage(): Promise<void> {
    if (offset.value === 0) return
    offset.value = Math.max(0, offset.value - limit)
    await load()
  }

  async function deleteConfig(id: number): Promise<'deleted' | 'blocked' | null> {
    deleteBlockedCount.value = 0
    try {
      const result = await flow.deleteConfig(id)
      if (result.kind === 'blocked') {
        deleteBlockedCount.value = result.insightsCount
        return 'blocked'
      }
      await load()
      return 'deleted'
    } catch (cause) {
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind !== 'canceled') error.value = normalized
      return null
    }
  }

  watch(canAuthor, () => void load(), { immediate: true })
  onScopeDispose(reset)

  return {
    configs: computed(() => configs.value),
    meta: computed(() => meta.value),
    total: computed(() => total.value),
    offset: computed(() => offset.value),
    state: computed(() => state.value),
    error: computed(() => error.value),
    draftFilters,
    deleteBlockedCount: computed(() => deleteBlockedCount.value),
    load,
    applyFilters,
    clearFilters,
    nextPage,
    previousPage,
    deleteConfig,
  }
}
